import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  AttachMoney,
  Business,
  CalendarToday,
  Description,
  Save,
  Cancel,
  Receipt,
} from '@mui/icons-material';

const EditCommission = () => {
  const history = useHistory();
  const location = useLocation();
  const commission = location.state?.commission;

  const [formData, setFormData] = useState({
    commissionId: commission?.commissionId || '',
    associateId: commission?.associateId || '',
    associateName: commission?.associateName || '',
    associateLevel: commission?.associateLevel || '',
    projectName: commission?.projectName || '',
    customerName: commission?.customerName || '',
    saleAmount: commission?.saleAmount || '',
    commissionRate: commission?.commissionRate || '',
    commissionAmount: commission?.commissionAmount || '',
    commissionType: commission?.commissionType || '',
    paymentStatus: commission?.paymentStatus || '',
    paymentDate: commission?.paymentDate || '',
    dueDate: commission?.dueDate || '',
    paymentMethod: commission?.paymentMethod || '',
    transactionId: commission?.transactionId || '',
    month: commission?.month || '',
    year: commission?.year || '',
    status: commission?.status || '',
    notes: commission?.notes || '',
    createdBy: commission?.createdBy || '',
    remark: commission?.remark || '',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    // Here you would typically save to backend
    console.log('Commission data:', formData);
    alert('Commission updated successfully!');
    history.goBack();
  };

  const handleCancel = () => {
    history.goBack();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
          <IconButton onClick={() => history.goBack()} sx={{ color: '#536DFE' }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
              Edit Commission
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update commission details for {commission?.associateName || 'associate'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Commission Info Card */}
      {commission && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#536DFE' }}>
              Commission Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ backgroundColor: '#E3F2FD', color: '#2196F3' }}>
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Associate Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {commission.associateName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business sx={{ color: '#666' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Project</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {commission.projectName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney sx={{ color: '#25D366' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Commission Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatCurrency(commission.commissionAmount)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday sx={{ color: '#666' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary">Due Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {commission.dueDate}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Commission Form */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#000' }}>
            Commission Details
          </Typography>

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#536DFE' }}>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Commission ID"
                value={formData.commissionId}
                disabled
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Associate ID"
                value={formData.associateId}
                disabled
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Associate Name"
                value={formData.associateName}
                disabled
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Associate Level"
                value={formData.associateLevel}
                disabled
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Project Name"
                value={formData.projectName}
                disabled
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Customer Name"
                value={formData.customerName}
                disabled
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sale Amount"
                type="number"
                value={formData.saleAmount}
                onChange={(e) => handleInputChange('saleAmount', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="Enter sale amount"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Commission Rate (%)"
                type="number"
                value={formData.commissionRate}
                onChange={(e) => handleInputChange('commissionRate', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="Enter commission rate"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Commission Amount"
                type="number"
                value={formData.commissionAmount}
                onChange={(e) => handleInputChange('commissionAmount', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="Enter commission amount"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Commission Type</InputLabel>
                <Select
                  value={formData.commissionType}
                  onChange={(e) => handleInputChange('commissionType', e.target.value)}
                  label="Commission Type"
                >
                  <MenuItem value="Direct Sale">Direct Sale</MenuItem>
                  <MenuItem value="Team Override">Team Override</MenuItem>
                  <MenuItem value="Referral">Referral</MenuItem>
                  <MenuItem value="Bonus">Bonus</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleInputChange('dueDate', e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Month"
                value={formData.month}
                onChange={(e) => handleInputChange('month', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="e.g., December 2024"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Year"
                type="number"
                value={formData.year}
                onChange={(e) => handleInputChange('year', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="2024"
              />
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Payment Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#536DFE' }}>
                Payment Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={formData.paymentStatus}
                  onChange={(e) => handleInputChange('paymentStatus', e.target.value)}
                  label="Payment Status"
                >
                  <MenuItem value="Paid">Paid</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                value={formData.paymentDate}
                onChange={(e) => handleInputChange('paymentDate', e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={formData.paymentMethod}
                  onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
                  label="Payment Method"
                >
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Online">Online</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Transaction ID"
                value={formData.transactionId}
                onChange={(e) => handleInputChange('transactionId', e.target.value)}
                variant="outlined"
                size="small"
                placeholder="Enter transaction ID"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Overdue">Overdue</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Created By"
                value={formData.createdBy}
                disabled
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                variant="outlined"
                size="small"
                multiline
                rows={2}
                placeholder="Additional notes about the commission..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remark"
                value={formData.remark}
                onChange={(e) => handleInputChange('remark', e.target.value)}
                variant="outlined"
                size="small"
                multiline
                rows={2}
                placeholder="Additional remarks..."
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handleCancel}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                backgroundColor: '#536DFE',
                '&:hover': {
                  backgroundColor: '#3f51b5',
                }
              }}
            >
              Save Changes
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EditCommission;
